import useOrganisation from "contexts/OrganisationContext/useOrganisation";
import useUserContext from "hooks/useUserContext";
import useCoaches from "providers/CoachesProvider/useCoaches";
import { useMemo } from "react";
import type { Coach } from "types/coach";

function useAuthorName(creatorId: string | null): string | null;
function useAuthorName(creatorIds: string[]): Record<string, string | null>;
function useAuthorName(creatorId: string | null | string[]): string | null | Record<string, string | null> {
  const org = useOrganisation();
  const [{ profile }] = useUserContext();
  const coachesStore = useCoaches();

  // Handle coach queries outside of useMemo
  const uniqueCreatorIds = useMemo(
    () => (Array.isArray(creatorId) ? Array.from(new Set(creatorId)) : [creatorId]),
    [creatorId]
  );

  const coachIds = useMemo(
    () => uniqueCreatorIds.filter((id) => id !== org.id && id !== null) as string[],
    [uniqueCreatorIds, org.id]
  );

  const coaches = useMemo(() => {
    const results: Coach[] = [];
    coachIds.forEach((id) => {
      const coach = coachesStore.find((c) => c.id === id);
      if (coach) {
        results.push(coach);
      }
    });
    return results;
  }, [coachIds, coachesStore]);

  return useMemo(() => {
    // Handle array of creatorIds
    if (Array.isArray(creatorId)) {
      const nameMap: Record<string, string | null> = {};

      // Always add the profile (owner) to the map
      if (profile?.id) {
        nameMap[profile.id] = profile.name;
      }

      // Add organization
      if (org.id) {
        nameMap[org.id] = org.name;
      }

      // Add coaches from the query results
      if (coaches) {
        coaches.forEach((coach) => {
          nameMap[coach.id] = coach?.name ?? null;
        });
      }

      // Ensure all requested IDs are in the map
      uniqueCreatorIds.forEach((id) => {
        if (id && !nameMap[id]) {
          nameMap[id] = null;
        }
      });

      return nameMap;
    }

    // Original single creatorId logic
    if (creatorId === profile?.id) {
      return profile.name;
    } else if (creatorId === org.id) {
      return org.name;
    } else if (creatorId && coaches) {
      return coaches[0]?.name ?? null;
    }

    return null;
  }, [creatorId, org.id, org.name, profile?.id, profile?.name, coaches, uniqueCreatorIds]);
}

export default useAuthorName;
